import axios from 'axios'
import {Auth} from '@/app/vuex/Auth'
import {EnvHelper} from '@/helpers/EnvHelper'
import {currentN3Network} from '@/helpers/NetworkModeRegistry'

interface RpcCacheEntry {
  url: string
  expiresAt: number
}

export abstract class RpcHelper {
  private static readonly CACHE_TTL_MS = 60_000
  private static readonly PROBE_TIMEOUT_MS = 4_000

  private static cache: Record<string, RpcCacheEntry> = {}
  private static globalLoad: Promise<unknown> | null = null

  static async ensureGlobalLoaded(): Promise<void> {
    if (Auth.global?.idGlobalPk) return
    if (!this.globalLoad) {
      this.globalLoad = Auth.loadGlobal().catch(() => undefined)
    }
    await this.globalLoad
  }

  static activeNetwork(): string {
    return currentN3Network()
  }

  static async getActiveRpc(): Promise<string> {
    await this.ensureGlobalLoaded()
    return this.getBestRpc(EnvHelper.rpcUrls(this.activeNetwork()))
  }

  static async getRpcFor(networkIdentifier: string): Promise<string> {
    await this.ensureGlobalLoaded()
    return this.getBestRpc(EnvHelper.rpcUrls(networkIdentifier))
  }

  static async getBestRpc(rpcUrls: string[]): Promise<string> {
    const candidates = rpcUrls.filter(Boolean)

    if (candidates.length === 0) {
      throw new Error('RpcHelper.getBestRpc: no RPC URLs configured')
    }

    if (candidates.length === 1) {
      return candidates[0]
    }

    const cacheKey = candidates.join('|')
    const now = Date.now()
    const cached = this.cache[cacheKey]
    if (cached && cached.expiresAt > now && candidates.includes(cached.url)) {
      return cached.url
    }

    const probes = await Promise.all(candidates.map(url => this.probe(url)))
    const healthy = probes.filter(
      (p): p is {url: string; height: number} => p !== null
    )

    if (healthy.length === 0) {
      throw new Error('RpcHelper.getBestRpc: no responsive RPC endpoint')
    }

    healthy.sort((a, b) => b.height - a.height)
    const best = healthy[0].url
    this.cache[cacheKey] = {url: best, expiresAt: now + this.CACHE_TTL_MS}
    return best
  }

  private static async probe(
    url: string
  ): Promise<{url: string; height: number} | null> {
    try {
      const {data} = await axios.post(
        url,
        {jsonrpc: '2.0', method: 'getblockcount', params: [], id: 1},
        {timeout: this.PROBE_TIMEOUT_MS}
      )
      const height = Number(data?.result)
      if (!Number.isFinite(height) || height <= 0) return null
      return {url, height}
    } catch {
      return null
    }
  }
}
